import { useState, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDatabase } from '@/entities/service';
import { useRooms } from '@/entities/room';
import { useProjectLibrary } from '@/entities/estimate';
import { useToastStore, useUIStore } from '@/stores';
import { servicesToExcel, parseExcelServices } from '@/utils/excelDatabase';
import { ProjectSnapshot } from '@/shared/model';
import type { MergeResult } from '@/entities/service';

export function useDashboardHandlers() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const addToast = useToastStore((s) => s.addToast);
    const openModal = useUIStore((s) => s.openModal);
    const [activeTab, setActiveTab] = useState<'projects' | 'catalog' | 'system'>('projects');

    // --- Hooks ---
    const database = useDatabase();
    const rooms = useRooms();
    const projectLibrary = useProjectLibrary();

    // --- State ---
    const [newProjectName, setNewProjectName] = useState('');
    const [editingProjectId, setEditingProjectId] = useState<string | number | null>(null);
    const [editingProjectName, setEditingProjectName] = useState('');
    
    // Modals
    const [isCreateServiceOpen, setIsCreateServiceOpen] = useState(false);
    const [isCreateUnitOpen, setIsCreateUnitOpen] = useState(false);
    const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
    const [projectIdToDelete, setProjectIdToDelete] = useState<string | number | null>(null);
    // SAFE-03: the merge computed by previewMergedServices, held until the user confirms or
    // cancels. Nothing has been committed to the store while this is non-null.
    const [importPreview, setImportPreview] = useState<MergeResult | null>(null);

    // --- Handlers: Projects ---
    const handleOpenProject = (project: ProjectSnapshot) => {
        rooms.loadProjectSnapshot(project);
        navigate('/editor');
    };

    const handleConfirmDeleteProject = () => {
        if (projectIdToDelete !== null) {
            projectLibrary.deleteProject(projectIdToDelete);
            setProjectIdToDelete(null);
            addToast(t('dashboard.messages.project_deleted', 'Проект удален'), 'success');
        }
    };

    const handleSaveCurrent = () => {
        if (!newProjectName.trim()) {
            addToast(t('dashboard.errors.enter_project_name', 'Введите название проекта'), 'error');
            return;
        }
        const snapshot = rooms.createProjectSnapshot(rooms.activeRoomId, newProjectName);
        projectLibrary.addProject(snapshot);
        // SAFE-06: make the saved snapshot the active project (mirrors StartPage.tsx:45-49),
        // so version history, commitVersion and autosave stop gating on a null activeProjectId.
        rooms.loadProjectSnapshot(snapshot);
        addToast(t('dashboard.messages.project_saved', 'Проект сохранен!'), 'success');
        setNewProjectName('');
    };

    const handleRenameProject = (id: string | number) => {
        if (editingProjectName.trim()) {
            projectLibrary.renameProject(id, editingProjectName);
            addToast(t('dashboard.messages.project_renamed', 'Переименовано'), 'success');
        }
        setEditingProjectId(null);
    };

    // --- Handlers: System ---
    const handleFullBackup = () => {
        const backup = {
            meta: { version: '2.0', type: 'full_system', date: new Date().toISOString() },
            database: {
                services: database.services,
                categories: database.categories,
                units: database.units,
                recipes: database.recipes
            },
            library: projectLibrary.library,
            activeProject: rooms.createProjectSnapshot(rooms.activeRoomId)
        };
        const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `builder_gen_backup_${new Date().toISOString().slice(0, 10)}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleFullRestore = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const data = JSON.parse(ev.target?.result as string);
                if (data.database) database.loadDatabaseFromObject(data.database);
                if (data.library) data.library.forEach((p: ProjectSnapshot) => projectLibrary.addProject(p));
                addToast(t('dashboard.messages.restore_success', 'Данные восстановлены!'), 'success');
            } catch {
                addToast(t('dashboard.errors.restore_failed', 'Ошибка файла бэкапа'), 'error');
            }
        };
        reader.readAsText(file);
    };

    // SAFE-03: parses the file and computes the merge (previewMergedServices), which mutates
    // nothing — the result is held for the Import Preview modal until the user confirms.
    const handleImportPriceList = async (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const data = await parseExcelServices(file);
            const result = database.previewMergedServices(data);
            setImportPreview(result);
        } catch {
            addToast(t('dashboard.errors.excel_error', 'Ошибка Excel'), 'error');
        } finally {
            // Reset so a cancel-then-retry with the very same file still fires a change event.
            // Without this, an unmodified <input> silently swallows the second attempt — the
            // exact class of silent failure SAFE-03 exists to close.
            e.target.value = '';
        }
    };

    // SAFE-03: commits the already-computed result (never recomputes it), so the numbers the
    // user confirmed are exactly the numbers written. The toast is built from what this call
    // returned, not from the preview's numbers, so it can never claim something the commit
    // did not actually do.
    const handleConfirmImport = () => {
        if (!importPreview) return;
        const stats = database.applyMergedServices(importPreview);
        addToast(t('dashboard.messages.import_stats', 'Импорт: +{{created}}, Обновлено: {{updated}}', { created: stats.created, updated: stats.updated }), 'success');
        setImportPreview(null);
    };

    const handleCancelImport = () => {
        setImportPreview(null);
    };

    const handleExportPriceList = async () => {
        const blob = await servicesToExcel(database.services);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = "price_list.xlsx";
        a.click();
        // Always revoke per project conventions (prevent memory leaks)
        URL.revokeObjectURL(url);
    };

    const handleResetSystem = () => {
        database.resetDatabase();
        rooms.resetRooms();
        setIsResetConfirmOpen(false);
        addToast(t('dashboard.messages.reset_success', 'Система сброшена'), 'info');
    };

    return {
        t,
        navigate,
        activeTab,
        setActiveTab,
        database,
        rooms,
        projectLibrary,
        newProjectName,
        setNewProjectName,
        editingProjectId,
        setEditingProjectId,
        editingProjectName,
        setEditingProjectName,
        isCreateServiceOpen,
        setIsCreateServiceOpen,
        isCreateUnitOpen,
        setIsCreateUnitOpen,
        isResetConfirmOpen,
        setIsResetConfirmOpen,
        projectIdToDelete,
        setProjectIdToDelete,
        importPreview,
        openModal,

        // Handlers
        handleOpenProject,
        handleConfirmDeleteProject,
        handleSaveCurrent,
        handleRenameProject,
        handleFullBackup,
        handleFullRestore,
        handleImportPriceList,
        handleConfirmImport,
        handleCancelImport,
        handleExportPriceList,
        handleResetSystem
    };
}